import React, { useRef, useState } from 'react';
import {
  Box,
  Slide,
  Typography,
  TypographyProps,
  colors,
  styled,
  Grid,
  Button,
} from '@mui/material';
import {
  AuthPermissions,
  AttachmentResponse,
  PaylinkDeliveryLog,
  PaylinkResponse,
} from 'skipify-types';
import { Stack, SxProps, useMediaQuery } from '@mui/system';
import { Email, Textsms } from '@mui/icons-material';

import { getDisplayedPaylinkUserInfo } from '../../../../util/helpers';
import { phone as phoneFormatter } from '../../../../util/formatters';
import { getAllowedActions, PaylinkActions } from '../../constants';
import { EditPaylinkForm } from '../EditPaylinkForm';
import {
  useCancelPaylinkMutation,
  useLazyGetAttachedFileQuery,
  useSendPaylinkMutation,
  useGetPaylinkQuery,
} from '../../../../store/modules/paylink/api';
import { toast } from '../../../../util/toast';
import { ActionDialog } from '../ActionDialog';
import { RefundPaylink } from '../RefundPaylink';
import { PaylinkSummary } from '../PaylinkSummary';
import { useRefundPaylink, usePaylinkUpdate } from '../../hooks';
import { useGetRefundsQuery } from '../../../../store/modules/orders/api';
import { ViewMorePopper } from '../ViewMorePopper';
import { useMerchant } from '../../../../contexts/MerchantContext';
import Attachment from '../Attachment';
import appTheme from '../../../../styles/themes/theme';
import { useGetPublicMerchantQuery } from '../../../../store/modules/merchant/api';

// Approximate length (in chars) of the View More button
const VIEW_MORE_LENGTH = 10;

type StyledTypography = React.ComponentType<TypographyProps<'span', { component?: 'span' }>>;
const SectionTitle: StyledTypography = styled(Typography)({
  fontSize: 12,
  color: 'grey.600',
  fontWeight: 600,
});

const SectionDescription: StyledTypography = styled(Typography)({
  fontSize: 14,
  color: 'black',
});

const SlideWrapper = styled(Box)({
  width: `100%`,
  // height: '600px',
  height: 'calc(100% - 2px)',
  position: 'absolute',
  backgroundColor: 'white',
  borderRadius: '8px',
  top: 0,
  left: 0,
});

export type PaylinkDetailsPage = 'details' | 'edit' | 'refund';
interface Props {
  merchantId: string;
  merchantName: string;
  paylink: PaylinkResponse;
  buttonColorClass?: string;
  defaultOpenPage?: PaylinkDetailsPage;
  slideWrapperStyles?: SxProps;
  dialogPaperStyles?: SxProps;
}

export const PaylinkDetails = ({
  merchantName,
  merchantId,
  paylink,
  buttonColorClass,
  slideWrapperStyles,
  dialogPaperStyles,
  defaultOpenPage = 'details',
}: Props) => {
  const { canAccess } = useMerchant();
  // Fetch the latest paylink data to ensure we have the most up-to-date status
  const { data: latestPaylink } = useGetPaylinkQuery(
    { paylinkId: paylink.paylinkId },
    { skip: false, refetchOnMountOrArgChange: true }, // Always fetch to ensure data freshness
  );

  // Use the latest data if available, otherwise fallback to the prop
  const currentPaylink = latestPaylink || paylink;

  const {
    customer,
    status,
    sendEmail,
    sendSms,
    total,
    orderTotal,
    approvedAmount,
    description,
    orderId,
    deliveryLogs,
    paylinkId,
    attachments,
    merchantInvoiceId: merchantReference,
    enablePartialPayment,
  } = currentPaylink;
  const effectiveOrderTotal = orderTotal ?? total;

  const allowedActions = getAllowedActions(status);
  const containerRef = useRef<HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(defaultOpenPage === 'edit');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [isResendDialogOpen, setIsResendDialogOpen] = useState<boolean>(false);
  const [sendPaylink, { isLoading: isSending }] = useSendPaylinkMutation();
  const [cancelPaylink, { isLoading: isCanceling }] = useCancelPaylinkMutation();
  const [isRefunding, setIsRefunding] = useState(defaultOpenPage === 'refund');
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));

  const MAX_LENGTH_DESCRIPTION = 16 + VIEW_MORE_LENGTH;

  const { data: refundData, isLoading: isLoadingRefunds } = useGetRefundsQuery(
    { orderId: orderId as string, merchantId },
    {
      skip:
        !orderId ||
        !(
          canAccess(AuthPermissions.REFUND_PAYLINKS) ||
          canAccess(AuthPermissions.REFUND_OWN_PAYLINKS)
        ) ||
        !isRefunding,
    },
  );

  const { onPaylinkUpdate, isLoading: isLoadingUpdate } = usePaylinkUpdate({
    paylinkId: currentPaylink.paylinkId,
  });
  const { onRefundPaylink, isLoading: isLoadingRefund } = useRefundPaylink({
    merchantId,
    orderId: orderId as string,
    paylinkId,
  });

  const [trigger, { isLoading: isLoadingAttachment }] = useLazyGetAttachedFileQuery();

  const { data: publicData } = useGetPublicMerchantQuery(merchantId, { skip: !merchantId });
  const currency = publicData?.acceptedCurrency;

  const handleCancel = () => {
    cancelPaylink({ paylinkId })
      .unwrap()
      .then(() => {
        setIsCancelDialogOpen(false);
        toast.success('Paylink successfully cancelled.');
      })
      .catch(() => {
        toast.error('Something went wrong, please try again later.');
      });
  };

  const handleResend = () => {
    sendPaylink({ paylinkId, sendEmail, sendSms })
      .unwrap()
      .then(() => {
        setIsResendDialogOpen(false);
        toast.success('Paylink Resent Successfully');
      })
      .catch(() => {
        toast.error('There was an error Resending this Paylink. Please try again.');
      });
  };

  const handleAttachedFileClick = (attachmentId: string) => {
    const newWindow = window.open('', '_blank');

    trigger({ paylinkId, attachmentId }, true)
      .unwrap()
      .then((fileData) => {
        const fileUrl = URL.createObjectURL(fileData);

        if (newWindow) {
          newWindow.location.href = fileUrl;
        } else {
          toast.error('There was an error downloading attachment. Please try again.');
        }
        URL.revokeObjectURL(fileUrl);
      })
      .catch(() => {
        toast.error('There was an error downloading attachment. Please try again.');
        if (newWindow) {
          newWindow.close();
        }
      });
  };

  const renderDeliveryLog = (log: PaylinkDeliveryLog) => {
    let text = '';
    let icon: JSX.Element | null = null;
    if (log.deliveryMethod === 'EMAIL') {
      text = `Email to ${customer.email} ${log.status.toLowerCase()}`;
      icon = <Email sx={{ fontSize: 14, color: 'grey.600' }} />;
    }
    if (log.deliveryMethod === 'SMS') {
      text = `SMS to ${phoneFormatter(customer.phone as string)} ${log.status.toLowerCase()}`;
      icon = <Textsms sx={{ fontSize: 14, color: 'grey.600' }} />;
    }
    return (
      <Stack direction="row" alignItems="center" spacing={0.5} key={log.deliveryLogId}>
        {icon}
        <SectionDescription>{text}</SectionDescription>
      </Stack>
    );
  };

  const renderUserInfoWithMask = () => {
    const { userInfoType, userInfo } = getDisplayedPaylinkUserInfo(paylink);
    let icon: JSX.Element | null = null;
    if (userInfoType === 'email') {
      icon = <Email sx={{ fontSize: 14 }} />;
    }

    if (userInfoType === 'phone') {
      icon = <Textsms sx={{ fontSize: 14 }} />;
    }

    if (!userInfo) {
      return <SectionDescription>N/A</SectionDescription>;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {icon}
        <SectionDescription>{userInfo}</SectionDescription>
      </Stack>
    );
  };

  const isDescriptionTooLong = !!description?.length && description.length > MAX_LENGTH_DESCRIPTION;
  const clippedDescription = isDescriptionTooLong
    ? `${description.substring(0, MAX_LENGTH_DESCRIPTION - VIEW_MORE_LENGTH)}...`
    : (description ?? 'N/A');

  const isMerchantReferenceTooLong =
    !!merchantReference?.length && merchantReference.length > MAX_LENGTH_DESCRIPTION;
  const clippedMerchantReference = isMerchantReferenceTooLong
    ? `${merchantReference.substring(0, MAX_LENGTH_DESCRIPTION - VIEW_MORE_LENGTH)}...`
    : (merchantReference ?? 'N/A');

  return (
    <>
      <Box sx={{ height: '100%', position: 'relative' }}>
        <Box
          ref={containerRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            py: 2,
            px: 3,
            gap: 4,
            height: '100%',
          }}
        >
          <Stack spacing={isMobile ? 1.5 : 4}>
            <PaylinkSummary
              status={status}
              approvedAmount={approvedAmount}
              orderTotal={effectiveOrderTotal}
              sendEmail={sendEmail}
              sendSms={sendSms}
              size={isMobile ? 'small' : 'medium'}
              enablePartialPayment={enablePartialPayment}
              currencyCode={currency?.code || 'USD'}
            />
            <Box>
              <SectionTitle>Customer</SectionTitle>
              {renderUserInfoWithMask()}
            </Box>
            <Grid container rowGap={1.5}>
              <Grid item xs={12} md={6}>
                <SectionTitle>Merchant Name</SectionTitle>
                <SectionDescription>{merchantName}</SectionDescription>
              </Grid>
              <Grid item xs={12} md={6}>
                <SectionTitle>Description</SectionTitle>
                <SectionDescription
                  component="span"
                  sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  {clippedDescription}
                </SectionDescription>
                <Box sx={{ display: 'inline-block' }}>
                  {isDescriptionTooLong && (
                    <ViewMorePopper title="Description">{description}</ViewMorePopper>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Grid container rowGap={1.5}>
              <Grid item xs={12} md={6}>
                <SectionTitle>Paylink ID</SectionTitle>
                <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                  <SectionDescription
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: `calc(100% - 100px)`,
                      marginTop: '2px',
                      [appTheme.breakpoints.down('sm')]: {
                        overflow: 'visible',
                        whiteSpace: 'normal',
                        maxWidth: '100%',
                      },
                    }}
                  >
                    {paylinkId}
                  </SectionDescription>
                  {!isMobile && <ViewMorePopper title="Paylink ID">{paylinkId}</ViewMorePopper>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <SectionTitle>Skipify OrderID</SectionTitle>
                <SectionDescription
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: `calc(100% - 40px)`,
                  }}
                >
                  {orderId ?? 'N/A'}
                </SectionDescription>
              </Grid>
            </Grid>
            <Grid container rowGap={1.5}>
              <Grid item xs={12} md={6}>
                <SectionTitle>Merchant Reference</SectionTitle>
                <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                  <SectionDescription component="span">
                    {clippedMerchantReference}
                  </SectionDescription>
                  {isMerchantReferenceTooLong && (
                    <ViewMorePopper title="Merchant Reference">{merchantReference}</ViewMorePopper>
                  )}
                </Box>
              </Grid>
            </Grid>
            <Box>
              <SectionTitle>{`History (${deliveryLogs.length})`}</SectionTitle>
              {paylink.deliveryLogs.map(renderDeliveryLog)}
            </Box>
            {attachments.length > 0 && (
              <Grid container>
                {attachments.map((attachment: AttachmentResponse) => {
                  return (
                    <Grid item key={attachment.attachmentId}>
                      <Attachment
                        attachment={attachment}
                        handleClick={handleAttachedFileClick}
                        isLoading={isLoadingAttachment}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Stack>
          {allowedActions.length > 0 && (
            <Box
              sx={{
                borderTop: `1px solid ${colors.grey[400]}`,
                paddingTop: 2,
                width: '100%',
                display: 'flex',
                justifyContent: allowedActions.length > 1 ? 'space-between' : 'center',
                alignItems: 'center',
                '& button': {
                  color: colors.grey[600],
                  fontSize: 14,
                  lineHeight: '18px',
                  fontWeight: 700,
                  textTransform: 'none',

                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
              }}
            >
              {allowedActions.includes(PaylinkActions.EDIT) && (
                <Button
                  variant="text"
                  onClick={() => setIsEditing(true)}
                  disabled={
                    !canAccess(AuthPermissions.UPDATE_PAYLINKS) &&
                    !canAccess(AuthPermissions.UPDATE_OWN_PAYLINKS)
                  }
                  className={buttonColorClass && buttonColorClass}
                  aria-label="Edit paylink"
                  data-testid="edit_paylink"
                >
                  Edit
                </Button>
              )}
              {allowedActions.includes(PaylinkActions.RESEND) && (
                <Button
                  variant="text"
                  onClick={() => setIsResendDialogOpen(true)}
                  className={buttonColorClass && buttonColorClass}
                  disabled={
                    !canAccess(AuthPermissions.CREATE_PAYLINKS) &&
                    !canAccess(AuthPermissions.CREATE_OWN_PAYLINKS)
                  }
                  data-testid="resend_paylink"
                >
                  Resend
                </Button>
              )}
              {allowedActions.includes(PaylinkActions.REFUND) && (
                <Button
                  variant="text"
                  disabled={
                    !canAccess(AuthPermissions.REFUND_PAYLINKS) &&
                    !canAccess(AuthPermissions.REFUND_OWN_PAYLINKS)
                  }
                  onClick={() => setIsRefunding(true)}
                >
                  Refund
                </Button>
              )}
              {allowedActions.includes(PaylinkActions.CANCEL) && (
                <Button
                  variant="text"
                  disabled={
                    !canAccess(AuthPermissions.CANCEL_PAYLINKS) &&
                    !canAccess(AuthPermissions.CANCEL_OWN_PAYLINKS)
                  }
                  onClick={() => setIsCancelDialogOpen(true)}
                  className={buttonColorClass && buttonColorClass}
                  data-testid="cancel_paylink"
                >
                  Cancel
                </Button>
              )}
            </Box>
          )}
        </Box>
        <Slide
          in={isEditing}
          direction="left"
          container={containerRef.current}
          mountOnEnter
          unmountOnExit
        >
          <SlideWrapper sx={slideWrapperStyles}>
            <EditPaylinkForm
              paylink={currentPaylink}
              onClose={() => setIsEditing(false)}
              onSubmit={onPaylinkUpdate}
              isLoadingSubmit={isLoadingUpdate}
            />
          </SlideWrapper>
        </Slide>
        <Slide
          in={isRefunding}
          direction="left"
          container={containerRef.current}
          mountOnEnter
          unmountOnExit
        >
          <SlideWrapper sx={slideWrapperStyles}>
            <RefundPaylink
              paylink={currentPaylink}
              onClose={() => setIsRefunding(false)}
              onSubmit={onRefundPaylink}
              isLoadingSubmit={isLoadingRefund}
              isLoadingTransactions={isLoadingRefunds}
              transactions={refundData?.transactions ?? []}
              currency={currency}
            />
          </SlideWrapper>
        </Slide>
      </Box>

      <ActionDialog
        isOpen={isCancelDialogOpen}
        onProceed={() => handleCancel()}
        isSubmitting={isCanceling}
        onClose={() => setIsCancelDialogOpen(false)}
        paperStyles={dialogPaperStyles}
        content={{
          heading: 'Are you sure you want to cancel this Paylink?',
          helpingText:
            'Your customer will no longer be able to complete this purchase if the Paylink request is cancelled.',
          cancelButtonText: 'Return to Details',
          proceedButtonText: 'Cancel Paylink',
        }}
      />
      <ActionDialog
        isOpen={isResendDialogOpen}
        onProceed={() => handleResend()}
        isSubmitting={isSending}
        onClose={() => setIsResendDialogOpen(false)}
        paperStyles={dialogPaperStyles}
        content={{
          heading: 'Are you sure you want to resend this Paylink?',
          helpingText:
            'Your customer will receive this same Paylink that has already been sent to them.',
          cancelButtonText: 'Cancel',
          proceedButtonText: 'Resend Paylink',
        }}
      />
    </>
  );
};
